import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import QuickButton from 'Douleutaras/components/QuickButton/QuickButton';
import { CustomInput } from 'Douleutaras/components/CustomInput/CustomInput';
import { Controller, useFormContext } from 'react-hook-form';
import { AttributeModelDataType, CategoryAttribute } from 'Douleutaras/redux/data/v2/categoryForm/types';
import { ChoiceItem } from 'Douleutaras/types/index';
import { CheckboxField } from 'Douleutaras/components/Forms/Fields/CheckboxField';
import { DateField } from 'Douleutaras/components/Forms/Fields/DateField';
import CustomError from 'Douleutaras/components/CustomError/CustomError';
import { ToggleField } from 'Douleutaras/components/Forms/Fields/ToggleField';
import { useTranslation } from 'react-i18next';
import ModalSelectionItem from 'Douleutaras/components/modals/parts/ModalSelectionItem';
import SkeletonLoader from 'Douleutaras/components/Loaders/SkeletonLoader';
import useQuestionStepHelpers, { decideNextQuestion } from 'Douleutaras/views/job/post-job/hooks/questions-step';
import { useAB } from 'Douleutaras/app/lib/ab';
import { PostHogEventNames } from 'Douleutaras/app/lib/posthog/posthog.provider';
import { useAppSelector } from 'Douleutaras/redux/hooks';
import { useFlowParam } from 'Douleutaras/hooks/jobs/jobFlow';
import { POST_JOB_FLOW } from 'Douleutaras/views/job/post-job/types';
import { FLOW_TYPE_TO_EVENT_PROP } from 'Douleutaras/constants/job';

import { StepStatus } from '../hooks/post-job';

export interface QuestionsRef {
  showNextButton: boolean;
  switchToNextStep(): void;
  switchStepBack?(): void;
}

export interface QuestionsStepProps {
  categoryId: string;
  showOnlyCallOutFeeQuestions: boolean;
  questionsStepHistory: Array<number>;
  setQuestionsStepHistory: React.Dispatch<React.SetStateAction<Array<number> | undefined>>;
  setShowNextButton: React.Dispatch<React.SetStateAction<boolean>>;
  setHasCallOutFeeStep?: React.Dispatch<React.SetStateAction<boolean>>;
  setStepStatus?: React.Dispatch<React.SetStateAction<StepStatus>>;
  onInnerProgressChange?(ratio: number): void;
  onLoadQuestion(question: CategoryAttribute): void;
  onSubmit(hasMoreQuestions: boolean): void;
}

enum UI {
  CHOICES_CHECKBOX = 'choices_checkbox',
  CHOICE_BUTTONS = 'choice_buttons'
}

const QuestionsStep = ({
                         categoryId,
                         showOnlyCallOutFeeQuestions,
                         onSubmit,
                         onLoadQuestion,
                         questionsStepHistory,
                         setQuestionsStepHistory,
                         setShowNextButton,
                         setStepStatus,
                         setHasCallOutFeeStep,
                         onInnerProgressChange
}: QuestionsStepProps, ref) => {

  const { t } = useTranslation();
  const [ questionsSorted, setQuestionsSorted ] = useState([]);
  const [ currentQuestion, setCurrentQuestion ] = useState<CategoryAttribute>();
  const [ sortedChoices, setSortedChoices ] = useState<Array<ChoiceItem>>();
  const { trigger, register, control, watch, setValue, formState: { errors, touchedFields } } = useFormContext();
  const dataType: AttributeModelDataType = currentQuestion?.attribute.datatype;
  const isMultiChoiceField = dataType === AttributeModelDataType.CHOICES;
  const { questions, categoryFormId } = watch();

  const {
    categoryForm,
    determineNextQuestion,
    filterCallOutFee,
    findQuestionById,
    setHistorySteps,
    onSelectChoice,
    questionId,
    sortedQuestions,
    switchToNextStep,
    switchStepBack
  } = useQuestionStepHelpers({ categoryId,
    questionsStepHistory,
    setQuestionsStepHistory,
    onSubmit,
    questionsSorted,
    setStepStatus,
    currentQuestion,
    showOnlyCallOutFeeQuestions,
    questions,
    watch
  });

  const ab = useAB();
  const postingFlowResponse = useAppSelector(state => state.pros.postingFlow);
  const flowParam = useFlowParam();
  const flowType = flowParam === POST_JOB_FLOW.MULTI_BID
    ? FLOW_TYPE_TO_EVENT_PROP.MULTI_SELECT
    : FLOW_TYPE_TO_EVENT_PROP.SINGLE_SELECT;

  // Call out fee determine
  useEffect(() => {
    if (categoryForm?.attributes.length > 0 && typeof setHasCallOutFeeStep === 'function') {
      const callOutFeeQuestions = categoryForm.attributes.filter(filterCallOutFee({ include: true }));
      // will try to find valid call out fee
      const callOutFeeQuestion = callOutFeeQuestions.find((callOutQuestion: CategoryAttribute) => {
        return decideNextQuestion({ userSelectedQuestions: questions, question: callOutQuestion });
      });

      if (callOutFeeQuestion !== undefined) {
        setHasCallOutFeeStep(true);
      } else {
        setHasCallOutFeeStep(false);
      }
    }
  }, [questionsSorted, currentQuestion]);

  // Triggers when QuestionStep is loaded after other steps
  useEffect(() => {
    let currentQuestionId = questionId;
    const questionsSortedEffect = sortedQuestions;

    // Should trigger if it comes from "back" action
    if (questionsSortedEffect.length > 0) {
      currentQuestionId = setHistorySteps(questionsSortedEffect, currentQuestionId);
    }

    const currentQuestionEffect = findQuestionById(currentQuestionId);

    setQuestionsSorted(questionsSortedEffect);
    setCurrentQuestion(currentQuestionEffect);
    onLoadQuestion(currentQuestionEffect);

    if (currentQuestionEffect) {
      const choicesEffect = [ ...currentQuestionEffect.attribute.choices ].sort((a, b) => a.order - b.order);
      setSortedChoices(choicesEffect);
    }

    // Set category formId
    if (!categoryFormId && categoryForm !== undefined) {
      setValue('categoryFormId', categoryForm.id);
    }

  }, [categoryForm, questionId]);

  useImperativeHandle(ref, (): Partial<QuestionsRef> => {
    return {
      switchToNextStep,
      switchStepBack: questionsStepHistory.length > 0 ? switchStepBack : undefined
    };
  }, [categoryForm, currentQuestion, onSubmit, questionsStepHistory, isMultiChoiceField]);

  useEffect(() => {
    setShowNextButton(isMultiChoiceField || ['text', 'date'].includes(dataType));
  }, [isMultiChoiceField, dataType]);

  const ui = currentQuestion?.attribute.ui; // choices_checkbox, choice_buttons
  const inputName = `questions.${currentQuestion?.attribute.id}`;

  useEffect(() => {
    if (typeof setStepStatus !== 'function') {
      return;
    }

    if (!currentQuestion?.attribute.required && questions?.[currentQuestion?.attribute.id]?.length === 0) {
      setStepStatus(StepStatus.CAN_SKIP);
      // Only show final button if this is the Call Out Fee question which is usually at the end of the form
    } else if (determineNextQuestion(currentQuestion?.id) === undefined && showOnlyCallOutFeeQuestions) {
      setStepStatus(StepStatus.IS_FINAL);
    } else {
      // Always show NEXT button, as there is always other steps before the end of the form
      setStepStatus(StepStatus.HAS_NEXT);
    }
  }, [questions[currentQuestion?.attribute.id]]);

  useEffect(() => {
    if (!onInnerProgressChange || showOnlyCallOutFeeQuestions) { return; }

    const total = questionsSorted?.length || 0;
    if (!total || !currentQuestion?.id) {
      onInnerProgressChange(0);

      return;
    }

    const idx = questionsSorted.findIndex((q: CategoryAttribute) => q?.id === currentQuestion.id);
    const ratio = Math.max(0, Math.min(1, total > 1 && idx >= 0 ? idx / (total - 1) : 0));
    onInnerProgressChange(ratio);
  }, [questionsSorted, currentQuestion, onInnerProgressChange, showOnlyCallOutFeeQuestions]);

  // Validate on every question change
  useEffect(() => {
    trigger();
  }, [currentQuestion]);

  useEffect(() => {
    if (showOnlyCallOutFeeQuestions) {
      // @ab PostHog event
      ab.capture(PostHogEventNames.VISIT_FEE_VIEWED, {
        category_slug: postingFlowResponse.category_slug,
        sub_category_slug: postingFlowResponse.sub_category_slug,
        business_unit_slug: postingFlowResponse.business_unit_slug,
        pro_selection_flow_type: flowType,
        bids_objective: postingFlowResponse.max_bids
      });
    }
  }, [showOnlyCallOutFeeQuestions]);

  if (!currentQuestion || !sortedChoices) {
    return <div>
      <ModalSelectionItem>
        <SkeletonLoader type="line" height={40} />
      </ModalSelectionItem>
      <ModalSelectionItem>
        <SkeletonLoader type="line" height={40} />
      </ModalSelectionItem>
      <ModalSelectionItem>
        <SkeletonLoader type="line" height={40} />
      </ModalSelectionItem>
    </div>;
  }

  return <div>
    { (dataType === AttributeModelDataType.CHOICE || dataType === AttributeModelDataType.CHOICES) &&
      <>
        <Controller
          rules={{
            validate: (value): boolean => {
              return isMultiChoiceField ? value?.length > 0 : true;
            }
          }}
          key={`question_${currentQuestion.attribute.id}_${isMultiChoiceField}`}
          control={control}
          name={inputName}
          render={({ field: { onChange, value } }) => {
            return <>
              { sortedChoices
                .map((choice, index) => {
                  const isSelected = Array.isArray(value) ? value.includes(String(choice.id)) : value === String(choice.id);

                  if (ui === UI.CHOICES_CHECKBOX) {
                    return <CheckboxField
                      key={`selection-item_${choice.id}`}
                      input={{
                        name: inputName,
                        title: choice.title,
                        value: isSelected,
                        // checkbox is never multi choice
                        onChange: () => onSelectChoice(choice.id,
                          false,
                          onChange,
                          isSelected,
                          value,
                          isMultiChoiceField,
                          () => trigger(inputName)
                        )
                      }}
                    />;
                  }

                  if (showOnlyCallOutFeeQuestions) {
                    return <ToggleField
                      key={`selection-item_${choice.id}`}
                      input={{
                        name: inputName,
                        title: choice.title,
                        value: isSelected,
                        // call out fee is multi choice
                        onChange: () => onSelectChoice(
                          choice.id,
                          false,
                          onChange,
                          isSelected,
                          value,
                          isMultiChoiceField,
                          () => trigger(inputName)
                        )
                      }}
                      toggleOnTheLeft
                    />;
                  }

                  return <ModalSelectionItem key={`selection-item_${choice.id}`}>
                    <QuickButton
                      title={choice.title}
                      selected={isSelected}
                      onClick={() => onSelectChoice(choice.id,
                        !isMultiChoiceField,
                        onChange,
                        isSelected,
                        value,
                        isMultiChoiceField,
                        () => trigger(inputName)
                      )}
                    />
                  </ModalSelectionItem>;
                })
              }
            </>;
          }}
        />
      </>
    }
    { dataType === AttributeModelDataType.TEXT &&
      <>
        <CustomInput id={`text_${currentQuestion.attribute.id}`}
                     name={`${currentQuestion.attribute.id}`}
                     placeholder={currentQuestion.attribute.placeholder}
                     input={{...register(`questions.${currentQuestion.attribute.id}`,
                         {
                           required: currentQuestion.attribute.required ? t('T.POST-JOB.REQUIRED FIELD') : false,
                           onChange: () => trigger(`questions.${currentQuestion.attribute.id}`)
                         }
                       )}}
                     meta={{
                       error: touchedFields.questions?.[currentQuestion.attribute.id]
                         ? errors.questions?.[currentQuestion.attribute.id]?.message as string
                         : undefined,
                       touched: touchedFields.questions?.[currentQuestion.attribute.id]
                     }}
        />
      </>
    }
    { dataType === AttributeModelDataType.DATE &&
      <>
        <Controller
          name={`questions.${currentQuestion.attribute.id}`}
          key={`question_${currentQuestion.attribute.id}`}
          rules={{ required: false }}
          render={({ field: { onChange }, fieldState: { error }}) => {
            const startDate = new Date();
            const minDate = new Date();

            startDate.setDate(startDate.getDate() + 1);
            minDate.setDate(minDate.getDate() + 1);

            return <>
              <DateField
                noDateByDefault
                input={{
                  onChange: val => onChange(val),
                  pickerProps: {
                    startDate,
                    minDate
                  }
                }}
              />
              { touchedFields.questions?.[currentQuestion.attribute.id] &&
                <CustomError>
                  {errors.questions?.[currentQuestion.attribute.id]?.message}
                </CustomError>
              }
            </>;
          }}
        />
      </>
    }
  </div>;
};

export default forwardRef(QuestionsStep);
